import { useEffect } from 'react';

import { Download } from 'lucide-react';

import { DocumentZipEvent } from '@/api/hooks/useDocumentZipEvents';
import { APIBaseChronos } from '@/api/hosts';
import { GenericExportProgressModal } from '@/components/molecules/GenericExportProgressModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  caseName: string;
  onStartZipCreation: () => void;
  onDownloadZip: () => Promise<void>;
  onCheckExisting: () => void;
  zipExists: boolean;
  zipInProgress: boolean;
  zipError?: string;
  hasActiveFilters?: boolean;
  totalDocuments?: number;
}

/**
 * Progress modal for document zip exports.
 * Thin wrapper around GenericExportProgressModal with document-zip-specific configuration.
 */
export const DownloadZipModal = ({
  isOpen,
  onClose,
  caseId,
  caseName,
  onStartZipCreation,
  onDownloadZip,
  onCheckExisting,
  zipInProgress,
  zipError,
  hasActiveFilters,
}: Props) => {
  // Call parent's check function when modal opens
  useEffect(() => {
    if (isOpen) {
      onCheckExisting();
    }
  }, [isOpen, onCheckExisting]);

  const readyDescription = hasActiveFilters
    ? `Filtered documents for case "${caseName}" have been packaged as a zip.`
    : `All documents for case "${caseName}" have been packaged as a zip.`;

  return (
    <GenericExportProgressModal<DocumentZipEvent>
      isOpen={isOpen}
      onClose={onClose}
      exportConfig={{
        progressEndpoint: `${APIBaseChronos}/api/storage/document-zip-events/${caseId}`,
      }}
      itemName={caseName}
      onDownload={onDownloadZip}
      isCreating={zipInProgress}
      error={zipError}
      readyDescription={readyDescription}
      downloadButtonText={`Download ${caseName}${hasActiveFilters ? ' (filtered)' : ''}.zip`}
      downloadButtonIcon={<Download className="mr-2" />}
      onRetry={onStartZipCreation}
      creatingText={`Creating ${hasActiveFilters ? 'filtered ' : ''}zip file...`}
    />
  );
};
